/* eslint-disable camelcase */
import { Alert, Box, Button, Checkbox, Grid, Paper, Skeleton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import { AssociationAndDistrictSelect } from 'src/shared/components/organisms/associationAndDistrictSelect';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useStatisticsContext } from 'src/shared/contexts/statisticsContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { memberSnapshot } from 'src/shared/services/memberSnapshot';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { ControlledAutocompleteDropdown } from 'src/shared/components/form/controlledAutocompleteDropdown';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { FormErrorSummary } from 'src/shared/components/form/formErrorSummary';
import { Download, InfoOutlined } from '@mui/icons-material';
import { MemberExportFormatEnum } from 'src/shared/models/enums/memberExportFormat.enum';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { Colors } from 'src/shared/components/styles/colors';
import { useExportLockHandler } from 'src/shared/functions/exportLockHandler';
import { ExportButtonEnum } from 'src/shared/models/enums/exportButton.enum';
import { useFetchSnapshots } from '../hooks/useFetchSnapshots';
import { SnapshotTimeResponse } from '../models/snapshotTimeResponse';
import { SnapshotResponse } from '../models/snapshotResponse';

export enum exportPropertiesEnum {
  active = 'Aktiva',
  interested = 'Intresseanmälda',
  resigned = 'Utträdda',
  member = 'Medlemmar',
}
interface ExportPropertyItem {
  propName: exportPropertiesEnum;
  value: boolean;
}

type FormValues = {
  snapshotTime: SnapshotTimeResponse;
  exportFormat: string;
  localAss_id: number;
  countyAss_id: number;
  exportProperties: ExportPropertyItem[];
};

export const StatisticsSnapshotPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const { lockExportBtnWhileCreating, unlockExportBtn, isExportButtonLocked } = useExportLockHandler();
  const { association, setAssociation, district, setDistrict } = useStatisticsContext();

  const [formattedOptions, setFormattedOptions] = useState<SnapshotTimeResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { spans, isSnapshotLoading } = useFetchSnapshots();

  const lockedExportButton = isExportButtonLocked(ExportButtonEnum.StatisticMemberRegisterExport);

  const exportOptions: { key: exportPropertiesEnum; label: string; infoText?: string }[] = [
    { key: exportPropertiesEnum.active, label: t('statistics:snapshot.active'), infoText: t('statistics:snapshot.activeInfo') },
    { key: exportPropertiesEnum.interested, label: t('statistics:snapshot.interested') },
    { key: exportPropertiesEnum.resigned, label: t('statistics:snapshot.resigned') },
    { key: exportPropertiesEnum.member, label: t('statistics:snapshot.member') },
  ];

  const requiredMessage = t('common:validationMessages.required');
  const memberSnapshotSchema: yup.Schema<FormValues> = yup.object().shape({
    snapshotTime: yup
      .mixed<SnapshotTimeResponse>()
      .nullable()
      .required(requiredMessage)
      .test('has-id', requiredMessage, (v) => v != null && v.snapshotTime_id != null),
    exportFormat: yup.string().required(),
    localAss_id: yup.number().required(),
    countyAss_id: yup.number().required(),
    exportProperties: yup
      .array()
      .of(
        yup.object().shape({
          propName: yup.mixed<exportPropertiesEnum>().required(),
          value: yup.boolean().required(),
        }),
      )
      .min(1, t('common:validationMessages.required'))
      .required(),
  });

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(memberSnapshotSchema),
    mode: 'all',
    defaultValues: {
      snapshotTime: undefined,
      exportFormat: MemberExportFormatEnum.CSV,
      localAss_id: association?.localAss_id || 0,
      countyAss_id: district?.countyAss_id || 0,
      exportProperties: undefined,
    },
  });

  const {
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = formMethods;

  const { append, remove } = useFieldArray({ control, name: 'exportProperties' });

  useEffect(() => {
    const updatedOptions = spans
      .filter((item) => item.snapshotTime_id !== 0)
      .map((item) => {
        const formattedDate = DateHelper.isValid(item.snapshotTime) ? DateHelper.getDate(item.snapshotTime) : item.snapshotTime;

        return {
          snapshotTime: formattedDate,
          snapshotTime_id: item.snapshotTime_id,
          type: item.type,
          comment: item.comment,
        };
      });

    setFormattedOptions(updatedOptions);
  }, [spans]);

  useEffect(() => {
    const currentExportProperties = watch('exportProperties') || [];
    const isMemberSet = currentExportProperties.some((prop) => prop.propName === exportPropertiesEnum.member);

    if (!isMemberSet) {
      setValue('exportProperties', [...currentExportProperties, { propName: exportPropertiesEnum.member, value: true }]);
    }
  }, [watch, setValue]);

  useEffect(() => {
    setValue('localAss_id', association?.localAss_id || 0);
    setValue('countyAss_id', district?.countyAss_id || 0);
  }, [association?.localAss_id, district?.countyAss_id, setValue]);

  const onExport = async (data: FormValues) => {
    setLoading(true);
    const dataToPost: SnapshotResponse = {
      snapshotTime_id: data.snapshotTime.snapshotTime_id,
      exportFormat: data.exportFormat,
      localAss_id: data.localAss_id ?? 0,
      countyAss_id: data.countyAss_id ?? 0,
      active: data.exportProperties.some((prop) => prop.propName === exportPropertiesEnum.active && prop.value) ?? false,
      interested: data.exportProperties.some((prop) => prop.propName === exportPropertiesEnum.interested && prop.value) ?? false,
      resigned: data.exportProperties.some((prop) => prop.propName === exportPropertiesEnum.resigned && prop.value) ?? false,
      member: data.exportProperties.some((prop) => prop.propName === exportPropertiesEnum.member && prop.value) ?? false,
    };

    try {
      lockExportBtnWhileCreating(ExportButtonEnum.StatisticMemberRegisterExport);
      await memberSnapshot(dataToPost);
      if (isMounted()) {
        toast.success(<>{t('member:exportInitialized')}</>);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        unlockExportBtn(ExportButtonEnum.StatisticMemberRegisterExport);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onExport)}>
        <Grid container spacing={3}>
          <Grid item xs>
            <MainContentHeader title={t('statistics:report.snapshot')} />
          </Grid>

          <Grid
            item
            xs='auto'
            sx={{
              pt: 2,
              pl: 2,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              '& .MuiAutocomplete-inputRoot': {
                backgroundColor: 'white',
              },
            }}
          >
            <AssociationAndDistrictSelect
              hideDistrict={currentUser?.districts.length === 0}
              association={association}
              district={district}
              associationCallback={(val) => setAssociation(val || undefined)}
              districtCallback={(val) => setDistrict(val || undefined)}
            />
          </Grid>
          <Grid item xs={12}>
            <Paper>
              <Grid>
                <PageFilterSection
                  leftContent={
                    <>
                      <Grid item xs={12}>
                        {isSnapshotLoading ? (
                          <Skeleton variant='rectangular' animation='wave' sx={{ height: '40px', width: '100%', borderRadius: '4px' }} />
                        ) : (
                          <>
                            <ControlledAutocompleteDropdown<SnapshotTimeResponse>
                              nameProp='snapshotTime'
                              name='snapshotTime'
                              options={formattedOptions}
                              callback={(e) => {
                                ConsoleHelper.log(e);
                              }}
                              label={t('statistics:snapshot.time')}
                              placeholder={t('common:button.choose')}
                            />
                            <Grid
                              item
                              xs={12}
                              display='flex'
                              flexWrap='wrap' // ✅ Allows checkboxes to wrap properly
                              mt={2}
                            >
                              {exportOptions.map((item) => {
                                const exportProperties = watch('exportProperties') || [];
                                const index = exportProperties.findIndex((prop) => prop.propName === item.key);
                                const checked = index !== -1 && exportProperties[index].value;

                                return (
                                  <Grid item xs='auto' sx={{ display: 'flex', alignItems: 'center' }} mr={2} key={item.key}>
                                    <Checkbox
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          append({ propName: item.key, value: true });
                                        } else {
                                          remove(index);
                                        }
                                      }}
                                      checked={checked}
                                    />
                                    <Box>{item.label}</Box>
                                    {item.infoText && (
                                      <Box sx={{ ml: 0.4, display: 'flex', alignItems: 'center' }}>
                                        <Tooltip placement='top' arrow title={item.infoText}>
                                          <InfoOutlined fontSize='small' sx={{ color: Colors.HeaderTextColor }} />
                                        </Tooltip>
                                      </Box>
                                    )}
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </>
                  }
                />
              </Grid>

              <Grid item xs={12} p={2}>
                <Tooltip placement='right' arrow title={lockedExportButton ? t('statistics:snapshot.exportInProgress') : ''}>
                  <span>
                    <Button
                      variant='contained'
                      endIcon={<Download fontSize='small' />}
                      disableElevation
                      disabled={loading || lockedExportButton}
                      type='submit'
                    >
                      {t('common:button.export')}
                    </Button>
                  </span>
                </Tooltip>

                <Grid item xs={5} mt={2}>
                  <FormErrorSummary translationNamespace='statistics' errors={errors} />
                </Grid>
                <Grid item xs={6} mt={2}>
                  {lockedExportButton && (
                    <Alert severity='warning' icon={false}>
                      {t('statistics:snapshot.exportAlertMessage')}
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
